import InputAdornment from '@mui/material/InputAdornment'
import type { StandardTextFieldProps } from '@mui/material/TextField'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import { useEffect, useRef, useState, type ElementType } from 'react'
import { debounce } from 'lodash'
import SearchIcon from '@mui/icons-material/Search'
import { type components } from 'api/playerPayback/api'

const resolveIconComponent = (iconSource: unknown): ElementType => {
  if (iconSource && typeof iconSource === 'object' && 'default' in iconSource) {
    return (iconSource as { default: ElementType }).default
  }

  return iconSource as ElementType
}

// Define a type that either refers to the schemas from components or a custom type
type SchemaType<T> = T extends keyof components['schemas']
  ? components['schemas'][T]
  : T

interface GridSearchFieldProps<T> extends StandardTextFieldProps {
  data: Array<SchemaType<T>>
  handleFilteredData: (filteredData: Array<SchemaType<T>>) => void
  numDaysUpdated?: boolean
  searchFields?: string[]
}

const getNestedValue = (obj: any, path: string): string | null => {
  // Split the path by dots to handle nested fields
  // e.g., 'licensedEstablishments.standardName'
  // This function will not work if the path includes array indices
  // For an example on how to handle arrays, see PromotionsPage.tsx and how the locationNames field is created from the licensedEstablishments array
  const keys = path.split('.')
  let value = obj

  for (const key of keys) {
    if (value && typeof value === 'object') {
      value = value[key]
    } else {
      return null
    }
  }

  return typeof value === 'string' ? value : null
}

export const GridSearchField = <T,>({
  data,
  handleFilteredData,
  numDaysUpdated,
  searchFields,
  ...props
}: GridSearchFieldProps<T>) => {
  const SearchIconComponent = resolveIconComponent(SearchIcon)
  const [searchTerm, setSearchTerm] = useState('')
  // ref to check that the boolean numDaysUpdated's value has actually changed, and not its identity (its a React thing)
  const prevNumDaysUpdatedRef = useRef(numDaysUpdated)

  const searchChange = (searchTerm: string) => {
    const filteredData = data.filter((item) => {
      if (typeof item !== 'object' || item === null) return false

      // If searchFields is provided, search only those fields
      if (searchFields && searchFields.length > 0) {
        return searchFields.some((field) => {
          const value = getNestedValue(item, field)
          return value?.toLowerCase().includes(searchTerm.toLowerCase())
        })
      }

      // Otherwise, use the original logic to search all fields
      const itemEntries = Object.entries(item) as Array<
        [
          string,
          (
            | string
            | number
            | { name?: string; standardName?: string }
            | null
            | undefined
          ),
        ]
      >

      // Check if any value in the item matches the formatted search term
      return itemEntries.some(([, value]) => {
        if (typeof value === 'string') {
          // Check if the value matches the formatted search term
          return value.toLowerCase().includes(searchTerm.toLowerCase())
        } else if (typeof value === 'number') {
          // Convert number to string and check
          return value.toString().includes(searchTerm)
        } else if (
          typeof value === 'object' &&
          value !== null &&
          ('name' in value || 'standardName' in value)
        ) {
          // Check if the object has a 'name' field and if it matches the search term
          return (
            (typeof value.name === 'string' &&
              value.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (typeof value.standardName === 'string' &&
              value.standardName
                .toLowerCase()
                .includes(searchTerm.toLowerCase()))
          )
        }
        return false
      })
    })

    handleFilteredData(filteredData)
  }

  // debounce search change when typing
  const debouncedHandleSearchChange = debounce((searchTerm: string) => {
    searchChange(searchTerm)
  }, 500)

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value
    setSearchTerm(searchTerm)
    debouncedHandleSearchChange(searchTerm)
  }

  useEffect(() => {
    // run searchChange when the numDays is updated in the PatronActivityLogPage
    if (prevNumDaysUpdatedRef.current !== numDaysUpdated) {
      searchChange(searchTerm)
      prevNumDaysUpdatedRef.current = numDaysUpdated
    }
  }, [numDaysUpdated])

  return (
    <Stack
      direction={{ md: 'row', sm: 'column', xs: 'column' }}
      gap={{ md: 0, sm: 2, xs: 2 }}
      mb={{ md: 4, sm: 2, xs: 2 }}
      justifyContent={'space-between'}
    >
      <TextField
        {...props}
        value={searchTerm}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {SearchIconComponent ? <SearchIconComponent /> : null}
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  )
}
